/**
 * PROTOTYPE CONTENT — NOT VERIFIED, NOT LEGALLY REVIEWED.
 *
 * Restored verbatim from `design-reference/`. These read as binding instruments
 * and they are not: the registration number, the addresses, the appointed
 * Information Officer and the retention periods below are all invented, and no
 * attorney has seen any of it.
 *
 * Publishing a PAIA manual naming an Information Officer who has not been
 * appointed, or a POPIA retention schedule the company does not follow, creates
 * real exposure under PAIA s51 and POPIA s55. These three documents must be
 * drafted or reviewed properly, and the Information Officer's appointment
 * recorded in `company/`, before the site goes live. See CONTENT-STATUS.md.
 */
export interface LegalSection {
  no: string
  h: string
  p: string
}

function numbered(items: [string, string][]): LegalSection[] {
  return items.map(([h, p], i) => ({ no: String(i + 1).padStart(2, '0'), h, p }))
}

/** Promotion of Access to Information Act 2 of 2000, section 51. */
export const paiaSections = numbered([
  [
    'Purpose of this manual',
    'This manual is published in terms of section 51 of the Promotion of Access to Information Act 2 of 2000 (PAIA). It tells any person how to request access to records held by Samloryx Engineering (Pty) Ltd, what records we hold, and which of them are available without a formal request.',
  ],
  [
    'Particulars of the company',
    'Samloryx Engineering (Pty) Ltd, registration number 2026/418772/07. Head office: Tubase Township, Burgersfort, Limpopo. Information Officer: Thandiwe Sithole, Financial Director. Postal address as above. Telephone +27 11 468 2200. Email paia@samloryx.co.za.',
  ],
  [
    'Guide of the Information Regulator',
    'A guide on how to use PAIA, compiled by the Information Regulator in terms of section 10, is available from the Regulator at 27 Stiemens Street, Braamfontein, Johannesburg, or at inforeg.org.za. It is available in each official language.',
  ],
  [
    'Records available without a request',
    'Company registration details, CIDB grading status, B-BBEE affidavit, tax clearance status, COIDA letter of good standing and published project information are provided on request to any prospective client or supplier without a formal PAIA application.',
  ],
  [
    'Categories of records held',
    'Statutory and company secretarial records; financial records and tax submissions; contract and tender documentation; drawings, specifications and as-built records; personnel and payroll records; health, safety and environmental records; plant and asset registers; insurance policies; supplier and subcontractor records.',
  ],
  [
    'How to request a record',
    'Complete Form 2 of the PAIA regulations and submit it to the Information Officer at the address above. Identify the record sought with enough particularity for us to locate it, state the form of access required and the right you are seeking to exercise or protect, and provide contact details for our reply.',
  ],
  [
    'Fees',
    'A request fee of R50.00 is payable by a requester other than a personal requester before the request is processed. Access fees for reproduction, search and preparation are charged at the rates prescribed in the PAIA regulations and are payable before the record is released.',
  ],
  [
    'Decision and remedies',
    'The Information Officer will decide within 30 days of receipt and notify you in writing, giving reasons for any refusal. Grounds for refusal include the mandatory protection of third-party privacy, commercial information, confidential information and records privileged in legal proceedings. You may lodge a complaint with the Information Regulator or apply to court in terms of section 78.',
  ],
  [
    'Records held under other legislation',
    'Records are also held in terms of the Companies Act 71 of 2008, the Income Tax Act 58 of 1962, the Value-Added Tax Act 89 of 1991, the Basic Conditions of Employment Act 75 of 1997, the Labour Relations Act 66 of 1995, the Occupational Health and Safety Act 85 of 1993, the Compensation for Occupational Injuries and Diseases Act 130 of 1993, the Mine Health and Safety Act 29 of 1996 and the CIDB Act 38 of 2000.',
  ],
  [
    'Availability and review',
    'This manual is available on this website, at our head office during office hours, and by email on request. It is reviewed annually and was last updated in August 2026.',
  ],
])

/** Protection of Personal Information Act 4 of 2013. */
export const privacySections = numbered([
  [
    'Who we are',
    'Samloryx Engineering (Pty) Ltd (registration 2026/418772/07) is the responsible party for the personal information described in this notice, as contemplated by the Protection of Personal Information Act 4 of 2013 (POPIA). Our Information Officer is Thandiwe Sithole, reachable at privacy@samloryx.co.za.',
  ],
  [
    'What we collect',
    'From clients and prospective clients: name, organisation, role, email, telephone and the project information you submit through the quote request. From job applicants: name, contact details, CV, qualifications, employment history and, where a role requires it, medical fitness and criminal record checks. From employees and site personnel: the records required by the OHS Act, MHSA, BCEA and COIDA. From suppliers: company details, banking details, statutory certificates and contact persons.',
  ],
  [
    'Why we process it',
    'To respond to enquiries and prepare estimates; to conclude and administer contracts; to comply with health and safety, tax, labour and CIDB obligations; to assess job applications; to verify and pay suppliers; and to maintain site access and induction records. We do not sell personal information and we do not use it for automated decision-making.',
  ],
  [
    'Legal basis',
    'We process personal information where it is necessary to conclude or perform a contract with you, where it is required by law, where it protects a legitimate interest of yours or ours, or where you have given consent. Consent may be withdrawn at any time by writing to privacy@samloryx.co.za, subject to any legal obligation we have to keep the record.',
  ],
  [
    'Sharing',
    'We share personal information with our clients where a contract requires site personnel records, with subcontractors and professional teams where the work requires it, with our auditors, bankers, insurers and attorneys, and with regulators including SARS, the Department of Employment and Labour, the CIDB and the Compensation Commissioner. Each recipient is required to protect it on terms no weaker than this notice.',
  ],
  [
    'Cross-border transfer',
    'Our email and file storage are hosted on servers within South Africa and the European Union. Where information leaves the Republic, it is transferred only to jurisdictions or recipients subject to laws or binding agreements providing an adequate level of protection under section 72 of POPIA.',
  ],
  [
    'Retention',
    'Contract, financial and tax records are retained for five years after completion or as otherwise required by law. Health and safety records are retained for the periods prescribed by the OHS Act and MHSA. Unsuccessful job applications are retained for twelve months and then deleted unless you ask us to keep them on file.',
  ],
  [
    'Security',
    'Access to personal information is limited to employees who need it for their role. Systems are password-protected with multi-factor authentication, laptops are encrypted, and site files containing personal information are kept in locked cabinets in the site office. Any compromise will be reported to the Information Regulator and to affected persons as required by section 22.',
  ],
  [
    'Your rights',
    'You may request confirmation of whether we hold your personal information, request a copy, ask us to correct or delete information that is inaccurate, irrelevant, excessive or obtained unlawfully, and object to processing. Requests are made on Form 2 under PAIA or by writing to privacy@samloryx.co.za. You may also complain to the Information Regulator at complaints.IR@inforegulator.org.za.',
  ],
  [
    'Website and cookies',
    'This website collects no personal information beyond what you type into the quote, contact or application forms. We do not use advertising or tracking cookies. Server logs record IP address and page requests for security purposes and are kept for 30 days.',
  ],
  [
    'Changes',
    'This notice was last updated in August 2026. Material changes will be published on this page and, where the change affects an active contract, communicated directly.',
  ],
])

export const supplierSections = numbered([
  [
    'Scope',
    'This code applies to every supplier, subcontractor, labour broker, plant hire company, consultant and service provider that contracts with Samloryx Engineering, and to their own subcontractors. Acceptance of a purchase order or subcontract constitutes acceptance of this code.',
  ],
  [
    'Legal compliance',
    'Suppliers must comply with all applicable South African law, including the Companies Act, the Labour Relations Act, the Basic Conditions of Employment Act, the OHS Act and its Construction Regulations, the Mine Health and Safety Act where applicable, tax legislation, and the National Environmental Management Act. Current statutory documents must be produced on request.',
  ],
  [
    'Health and safety',
    'No person may work on a Samloryx site without induction, valid medical certification where required, and the correct personal protective equipment. Suppliers must submit a health and safety file before mobilising, appoint competent supervision in writing, and report every incident, no matter how minor, on the day it occurs. Every person on our sites holds stop-work authority and may not be penalised for exercising it.',
  ],
  [
    'Labour practices',
    'No child labour and no forced labour, directly or through any intermediary. Wages must meet or exceed the Bargaining Council for the Civil Engineering Industry rates or the applicable sectoral determination, and be paid on time and in full. Working hours, overtime and leave must comply with the BCEA. Freedom of association is respected.',
  ],
  [
    'Non-discrimination and harassment',
    'Suppliers must not discriminate on the grounds listed in section 9 of the Constitution. Harassment, intimidation and victimisation on our sites are grounds for immediate removal of the individual concerned and may terminate the subcontract.',
  ],
  [
    'Anti-bribery and conflicts of interest',
    'Samloryx neither offers nor accepts bribes, kickbacks, facilitation payments or improper gifts. Suppliers must not offer anything of value to a Samloryx employee to influence a procurement decision. Any personal relationship between a supplier and a Samloryx employee that could affect a decision must be declared in writing before quoting.',
  ],
  [
    'Fair competition',
    'Collusive tendering, bid rigging, price fixing and market division are prohibited by the Competition Act 89 of 1998 and will be reported. Suppliers must price independently and must not disclose our enquiry documents to competing bidders.',
  ],
  [
    'Environment',
    'Suppliers must work within the Environmental Management Programme for the site, contain and report spills immediately, dispose of waste only at permitted facilities and provide safe disposal certificates, and prevent any discharge to watercourses. Rehabilitation obligations run with the subcontract.',
  ],
  [
    'Quality and materials',
    'Materials must meet the specification and be accompanied by the relevant SANS certificates, batch records or test results. Substitution requires our written approval before delivery. Non-conforming work is repaired or replaced at the supplier’s cost.',
  ],
  [
    'Transformation and local content',
    'We prioritise suppliers that are B-BBEE compliant and, where the contract has local content or SMME development obligations, suppliers that can demonstrate genuine local employment and ownership. Fronting arrangements disqualify a supplier permanently.',
  ],
  [
    'Confidentiality and data',
    'Drawings, bills of quantities, rates and client information provided to a supplier remain our property and may not be disclosed or reused. Personal information exchanged between the parties must be processed in accordance with POPIA.',
  ],
  [
    'Raising a concern',
    'Any person may report a breach of this code, in confidence and without fear of retaliation, to conduct@samloryx.co.za or by calling +27 11 468 2200 and asking for the Financial Director. Reports may be made anonymously. Substantiated breaches result in corrective action, removal from our approved supplier list, or termination.',
  ],
])

export const legalMeta = {
  paiaVersion: 'Version 1.0, August 2026',
  privacyVersion: 'Last updated August 2026',
  supplierVersion: 'Version 1.0, August 2026',
  /** Split so the email renders as a link, as it did in the prototype. */
  paiaSubmitBefore:
    'Form 2 requests go to the Information Officer, Tubase Township, Burgersfort, Limpopo, or ',
  paiaSubmitLink: 'paia@samloryx.co.za',
  paiaSubmitAfter: '. We acknowledge receipt within five working days.',
  informationOfficer:
    'Thandiwe Sithole · privacy@samloryx.co.za · +27 11 468 2200. Complaints may also be lodged with the Information Regulator (South Africa), 27 Stiemens Street, Braamfontein.',
  supplierIntro:
    'We are a young company. The suppliers we appoint now will shape what clients think of us for the next decade, so we would rather be explicit than polite about what we expect.',
  supplierRegister:
    'Send company registration, tax clearance, B-BBEE affidavit or certificate, COIDA letter of good standing and banking confirmation to suppliers@samloryx.co.za.',
  supplierReport:
    'conduct@samloryx.co.za · +27 11 468 2200. Reports may be anonymous. No person raising a concern in good faith will suffer any consequence for doing so.',
}
